import React, { useState, useEffect } from "react";
import { 
  Download, 
  Shield, 
  Terminal, 
  Users, 
  AlertCircle, 
  CheckCircle,
  Info,
  Clock,
  Server,
  Laptop,
  Key,
  Settings,
  ExternalLink,
  RefreshCw,
  X,
  Phone,
  Mail
} from "lucide-react";
import { apiService } from "../../services/apiService";
import { authService } from "../../services/authService";
import config from "../../config.json";

interface SupportTool {
  id: string;
  name: string;
  description: string;
  type: "rmm_agent" | "remote_assist" | "meshcentral" | "diagnostic";
  icon: React.ReactNode;
  requiresCustomBuild: boolean;
  oneTimeUse: boolean;
  platform?: "windows" | "mac" | "linux" | "all";
  fileSize?: string;
  downloadCount?: number;
  documentation?: string;
}

interface DownloadLink {
  url: string;
  expiresAt: string;
  token: string;
}

interface SupportToolsTabProps {
  customerId: string;
  customerName: string;
}

const SupportToolsTab: React.FC<SupportToolsTabProps> = ({ customerId, customerName }) => {
  const [tools, setTools] = useState<SupportTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingTools, setDownloadingTools] = useState<Set<string>>(new Set());
  const [generatingLinks, setGeneratingLinks] = useState<Set<string>>(new Set());
  const [downloadLinks, setDownloadLinks] = useState<Map<string, DownloadLink>>(new Map());
  const [downloadHistory, setDownloadHistory] = useState<any[]>([]);
  const [showNAbleInfo, setShowNAbleInfo] = useState(false);
  const [showDownloadToast, setShowDownloadToast] = useState(false);
  const [downloadedFileName, setDownloadedFileName] = useState("");

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    setLoading(true);
    try {
      const response = await apiService.getSupportTools();
      if (!response.errors && response.result) {
        // Map the API response to include icons based on tool type
        const toolsWithIcons = response.result.map((tool: any) => ({
          ...tool,
          icon: getToolIcon(tool.type),
          platform: tool.platforms?.[0] || "windows",
          requiresCustomBuild: tool.requires_custom_build,
          oneTimeUse: tool.one_time_use,
          fileSize: tool.file_size
        }));
        setTools(toolsWithIcons);
      }

      // Also load download history
      const historyResponse = await apiService.getToolDownloadHistory();
      if (!historyResponse.errors && historyResponse.result) {
        setDownloadHistory(historyResponse.result);
      }
    } catch (error) {
      console.error("Failed to load support tools:", error);
    } finally {
      setLoading(false);
    }
  };

  const getToolIcon = (type: string) => {
    switch (type) {
      case "rmm_agent":
        return <Shield className="h-6 w-6 text-blue-600" />;
      case "remote_assist":
        return <Users className="h-6 w-6 text-green-600" />;
      case "meshcentral":
        return <Terminal className="h-6 w-6 text-purple-600" />;
      case "diagnostic":
        return <Settings className="h-6 w-6 text-orange-600" />;
      default:
        return <Download className="h-6 w-6 text-gray-600" />;
    }
  };

  const handleGenerateDownload = async (toolId: string, osType?: string) => {
    setGeneratingLinks(prev => new Set(prev).add(toolId));
    
    try {
      // For ITP RMM, the backend now downloads and returns the file directly
      if (toolId === "nable-rmm") {
        // Use the config to get the correct API URL
        const baseUrl = config.prod ? config.urlBase.prod : config.urlBase.dev;
        const response = await fetch(`${baseUrl}/crm/support-tools/generate-link`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authService.getAuthToken()}`
          },
          body: JSON.stringify({ tool_id: toolId, os_type: osType })
        });
        
        // Check if it's an error response (JSON)
        const contentType = response.headers.get("content-type");
        
        if (contentType && contentType.includes("application/json")) {
          // This is likely an error response
          const data = await response.json();
          
          // Debug: Log the actual response structure
          console.log("🔍 ITP RMM error response:", data);
          
          // Check for configuration error at different levels
          const errorType = data.result?.error || data.error;
          
          if (errorType === "configuration_not_found") {
            // Show ITP RMM configuration error modal
            console.log("🔍 Showing ITP RMM info modal");
            setShowNAbleInfo(true);
          } else {
            console.error("Error response:", data);
            alert(data.result?.message || data.message || data.error_message || "Failed to download tool");
          }
          return;
        }
        
        // It's a file download
        if (response.ok) {
          const blob = await response.blob();
          // Get filename from response headers
          const contentDisposition = response.headers.get("content-disposition");
          let filename = null;
          
          if (contentDisposition) {
            // Parse: attachment; filename="ITP_RMM_Agent_Name.zip"
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/"/g, "");
            }
          }
          
          // Debug: Log the parsing results
          console.log("🔍 Content-Disposition:", contentDisposition);
          console.log("🔍 Parsed filename:", filename);
          
          if (!filename) {
            // This should rarely happen now, but keep as fallback
            filename = "ITP_RMM_Agent.zip";
            console.log("🔍 Using fallback filename:", filename);
          }
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          
          // Track download
          await apiService.trackToolDownload(toolId, "direct-download");
          
          // Show success toast
          setDownloadedFileName(filename);
          setShowDownloadToast(true);
          
          // Auto-hide toast after 5 seconds
          setTimeout(() => {
            setShowDownloadToast(false);
          }, 5000);
          
          // Reload download history
          const historyResponse = await apiService.getToolDownloadHistory();
          if (!historyResponse.errors && historyResponse.result) {
            setDownloadHistory(historyResponse.result);
          }
        } else {
          console.error("Download failed:", response.status);
          alert("Failed to download tool");
        }
      } else {
        // For other tools, use the old URL-based approach (for now)
        const response = await apiService.generateToolDownloadLink(toolId);
        
        if (!response.errors && response.result) {
          const link: DownloadLink = {
            url: response.result.url,
            expiresAt: response.result.expires_at,
            token: response.result.token
          };
          
          setDownloadLinks(prev => new Map(prev).set(toolId, link));
        } else {
          console.error("Failed to generate download link:", response.errors);
        }
      }
    } catch (error) {
      console.error("Failed to generate download:", error);
      alert("Error downloading tool");
    } finally {
      setGeneratingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(toolId);
        return newSet;
      });
    }
  };

  const handleDownload = async (tool: SupportTool) => {
    const link = downloadLinks.get(tool.id);
    if (!link) {
      await handleGenerateDownload(tool.id);
      return;
    }

    setDownloadingTools(prev => new Set(prev).add(tool.id));
    
    try {
      // Track download in backend
      await apiService.trackToolDownload(tool.id, link.token);
      
      // For ITP RMM, the download is already handled in handleGenerateDownload
      if (tool.id === "nable-rmm") {
        // Download already happened, just return
        return;
      }
      
      // For other tools, open the download URL
      const downloadUrl = link.url;
      window.open(downloadUrl, "_blank");
      
      // For one-time use tools, invalidate the link after download
      if (tool.oneTimeUse) {
        setDownloadLinks(prev => {
          const newMap = new Map(prev);
          newMap.delete(tool.id);
          return newMap;
        });
      }
      
      // Reload download history
      const historyResponse = await apiService.getToolDownloadHistory();
      if (!historyResponse.errors && historyResponse.result) {
        setDownloadHistory(historyResponse.result);
      }
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloadingTools(prev => {
        const newSet = new Set(prev);
        newSet.delete(tool.id);
        return newSet;
      });
    }
  };

  const formatFileSize = (size: string) => {
    return size;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Loading support tools...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900">About Support Tools</h4>
            <p className="text-sm text-blue-700 mt-1">
              Download customized support tools for <span className="font-semibold">{customerName}</span>. 
              Some tools are built specifically for your organization and include your unique identifiers for proper monitoring and management.
            </p>
          </div>
        </div>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {tools.map((tool) => {
          const isGenerating = generatingLinks.has(tool.id);
          const isDownloading = downloadingTools.has(tool.id);
          const hasLink = downloadLinks.has(tool.id);
          const link = downloadLinks.get(tool.id);

          return (
            <div 
              key={tool.id} 
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      {tool.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{tool.name}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        {tool.oneTimeUse && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Clock className="h-3 w-3 mr-1" />
                            One-Time Use
                          </span>
                        )}
                        {tool.requiresCustomBuild && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <Key className="h-3 w-3 mr-1" />
                            Custom Build
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">{tool.description}</p>

                {/* Tool Info */}

                {/* Download Link Status */}
                {hasLink && link && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Download link ready</span>
                      </div>
                      <span className="text-xs text-green-600">
                        Expires: {formatDate(link.expiresAt)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col space-y-3">
                  {!hasLink && tool.requiresCustomBuild && tool.id === "nable-rmm" ? (
                    <>
                      <div className="text-sm font-medium text-gray-700">Choose your operating system:</div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleGenerateDownload(tool.id, "windows")}
                          disabled={isGenerating}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                        >
                          {isGenerating ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4 mr-1" />
                          )}
                          <span>Windows</span>
                        </button>
                        <button
                          onClick={() => handleGenerateDownload(tool.id, "mac")}
                          disabled={isGenerating}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                        >
                          {isGenerating ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4 mr-1" />
                          )}
                          <span>macOS</span>
                        </button>
                        <button
                          onClick={() => handleGenerateDownload(tool.id, "linux")}
                          disabled={isGenerating}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm"
                        >
                          {isGenerating ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4 mr-1" />
                          )}
                          <span>Linux</span>
                        </button>
                      </div>
                    </>
                  ) : !hasLink && tool.requiresCustomBuild ? (
                    <button
                      onClick={() => handleGenerateDownload(tool.id)}
                      disabled={isGenerating}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          <span>Building Custom Installer...</span>
                        </>
                      ) : (
                        <>
                          <Settings className="h-4 w-4 mr-2" />
                          <span>Generate Custom Build</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDownload(tool)}
                      disabled={isDownloading || isGenerating}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      {isDownloading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          <span>Downloading...</span>
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          <span>Download</span>
                        </>
                      )}
                    </button>
                  )}

                  {tool.documentation && (
                    <a
                      href={tool.documentation}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Download History - Hidden until proper download tracking is implemented */}
      {false && (
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Downloads</h3>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tool
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Downloaded By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {downloadHistory.length > 0 ? (
                downloadHistory.map((download: any, index: number) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {download.tool_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {download.downloaded_by}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(download.download_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        download.status === "completed" 
                          ? "bg-green-100 text-green-800" 
                          : "bg-gray-100 text-gray-800"
                      }`}>
                        {download.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-6 py-4 text-sm text-gray-500 text-center" colSpan={4}>
                    No download history available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* ITP RMM Configuration Info Modal */}
      {showNAbleInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <Info className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">ITP RMM Download</h3>
                </div>
                <button
                  onClick={() => setShowNAbleInfo(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                <p className="text-gray-700">
                  Your download should begin shortly. The installer will be customized for <span className="font-semibold">{customerName}</span>.
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-900 mb-1">Configuration Not Found?</p>
                      <p className="text-amber-700">
                        If you receive an error message, it means your account name in our system doesn't exactly match the name in our RMM system.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Need Assistance?</p>
                  <div className="space-y-2">
                    <a 
                      href="tel:1-888-847-9266" 
                      className="flex items-center text-sm text-blue-700 hover:text-blue-800"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      1-888-847-9266
                    </a>
                    <a 
                      href="mailto:support@itp360.com" 
                      className="flex items-center text-sm text-blue-700 hover:text-blue-800"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      support@itp360.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowNAbleInfo(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Download Success Toast */}
      {showDownloadToast && (
        <div className="fixed top-4 right-4 z-50 max-w-sm transform transition-all duration-300 ease-in-out">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Download className="h-4 w-4 text-green-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-gray-900 mb-1">
                  Download Started!
                </h4>
                <p className="text-xs text-gray-600 mb-2">
                  {downloadedFileName}
                </p>
                <div className="flex items-center text-xs text-gray-500">
                  <span>Check your downloads folder or look for the download notification</span>
                </div>
                <div className="flex items-center mt-2 text-xs text-blue-600">
                  <span>💡 Chrome: Top-right corner • Firefox: Downloads panel • Safari: Downloads button</span>
                </div>
              </div>
              <button
                onClick={() => setShowDownloadToast(false)}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportToolsTab;
